/**
 * The web app manifest, built from the tenant's config at runtime instead of a
 * file per tenant in public/. Used when the tenant has `appIcons` (square PNGs
 * on the image CDN, keyed by size); a tenant without them keeps its static
 * /<tenant>manifest.json.
 *
 * The manifest is served from a blob: URL, so start_url and scope are absolute:
 * relative ones would resolve against the blob, not the site.
 */

export type AppIcons = Partial<Record<"180" | "192" | "512", string>>;

const MANIFEST_SIZES = ["192", "512"] as const;

export function buildWebManifest(
  config: { title?: string; name?: string; appIcons?: AppIcons },
  origin: string,
) {
  const name = config.title || config.name || "";
  return {
    name,
    short_name: name,
    start_url: `${origin}/`,
    scope: `${origin}/`,
    display: "standalone",
    background_color: "#ffffff",
    theme_color: "#ffffff",
    icons: MANIFEST_SIZES.filter((size) => config.appIcons?.[size]).map((size) => ({
      src: config.appIcons![size] as string,
      sizes: `${size}x${size}`,
      type: "image/png",
    })),
  };
}

/**
 * A URL for the tenant's manifest: a blob: URL built from its appIcons, or
 * `fallback` when it has none (or the browser cannot make blob URLs).
 */
export function webManifestUrl(
  config: { title?: string; name?: string; appIcons?: AppIcons } | undefined,
  fallback: string,
): string {
  if (!config?.appIcons || typeof URL.createObjectURL !== "function") return fallback;
  const manifest = buildWebManifest(config, window.location.origin);
  return URL.createObjectURL(
    new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" }),
  );
}
